<template lang="pug">
listbox.uc-language-widget.inline-block.dark(
  :value="value"
  @update:modelValue="onChange"
  dir="ltr"
  v-if="languages?.length > 0"
  v-slot="{ open }"
)
  .relative.dark
    watch-boolean-slot(:boolean="open" :off="resetPreview")

    listbox-button.overflow-hidden.flex.items-stretch.justify-start.shadow.bg-widget.px-4.py-2.pr-8.text-gray-100.transition-colors.ripple.rounded.rounded-b-none(
      class="hover_bg-primary-900/80 focus-visible_bg-primary-900/80"
    )
      span.inline-flex.items-center.prose.font-normal
        uc-icon.mr-1.-ml-1.text-lg.text-white(icon="mdi:web")
        span.px-1(:class="`text-gray-100`") {{ labels[value] }}
    transition(
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-200 ease-out"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    )
      listbox-options.absolute.bottom-11.min-w-full.py-1.mt-1.overflow-auto.text-base.rounded.shadow-lg.ring-1.ring-black.ring-opacity-5.focus_outline-none.sm_text-sm.w-auto.simple-scrollbar.text-white.bg-widget.simple-scrollbar.overflow-y-auto(
        @mouseleave="resetPreview"
        class="max-h-[70vh]"
      )
        listbox-option(
          :key="lang"
          :value="lang"
          as="template"
          v-for="lang in languages"
          v-slot="{ active }"
        )
          li.text-base.flex.items-stretch.justify-start.cursor-default.select-none.transition-colors.duration-200.pr-4.px-1.ripple(
            :class="[{ 'bg-primary-500/40': active }]"
          )
            watch-boolean-slot(:boolean="active" :on="() => onActive(lang)")
            span.inline-flex.items-center
              uc-icon.text-primary-500.auto-alpha-0.mx-2.text-lg(
                :class="{ 'auto-alpha-1': lang === value }"
                icon="mdi:check"
              )
            span.inline-flex.min-w-max.items-center.prose.font-normal
              span.text-gray-100.py-2.pr-4 {{ labels[lang] }}
</template>

<script setup lang="ts">
import { defineComponent, ref } from 'vue';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue';
import useContent from '../stores/useContent';
import useSuspend from '../lib/useSuspend';
import languagesData from '../lib/languages.json';
import { set } from '../lib/SCORM';
import { storeToRefs } from 'pinia';
import useEditMode from '../stores/useEditMode';
import openLightbox from '../lib/openLightbox';
import isEditModeAvailable from '../lib/isEditModeAvailable';
import { commitClientEdits } from '../lib/callModContent';
import useRequestStatus from '../lib/useRequestStatus';

const { statuses, set: setStatus } = useRequestStatus();
const { enabled } = storeToRefs(useEditMode());

const labels = {};

Object.keys(languagesData).forEach(k => {
  labels[k] = languagesData[k].name;
});

const suspend = useSuspend();
const { content } = useContent();
const { languages } = content;

const value = ref(suspend.locale);

if (languages?.length > 0 && !languages?.includes('en-GB'))
  languages.unshift('en-GB');

const setLocale = (v: string) => {
  try {
    // set the language and direction attributes on the <html> element
    document.documentElement.lang = v;
    document.documentElement.dir = languagesData[v].rtl ? 'rtl' : 'ltr';

    set('locale', v);
  } catch (e) {
    console.error(e);
  }
};

if (suspend.locale) {
  setLocale(suspend.locale);
}

const resetPreview = () => {
  if (value.value !== suspend.locale) {
    setLocale(value.value);
  }
};

const onActive = (v: string) => {
  setLocale(v);
};

const hasTranslations = () => {
  return useContent().compiled.languages?.length;
};

const onChange = (v: string) => {
  value.value = v;

  if (
    isEditModeAvailable &&
    useContent().translationsInvalidated &&
    hasTranslations()
  ) {
    // reset any previous re-run translations status
    setStatus('lightbox-rerun-translations', {
      pending: false,
      idle: true,
      success: false
    });

    openLightbox('lightbox-rerun-translations');
  }

  setLocale(v);
  document.dispatchEvent(new CustomEvent('localeChanged', { detail: v }));
};

const WatchBooleanSlot = defineComponent({
  props: {
    boolean: { type: Boolean, required: true },
    on: { type: Function, default: () => null },
    off: { type: Function, default: () => null }
  },
  watch: {
    boolean(v: boolean) {
      v ? this.on() : this.off();
    }
  },
  render: () => null
});
</script>
